import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { cx } from './cx'
import { Textarea } from './Textarea'
import s from './MentionField.module.css'

/**
 * MentionField — content that builds the graph. A note/minutes field where typing `@`
 * opens a cross-entity search (the caller supplies `onSearch`, typically the registry
 * Lookup); picking a result inserts an @Label into the text AND records a resolved
 * mention `{entity_type, id, label}`. The mentions become graph edges on save, so writing
 * naturally — "agreed with @Acme to extend @Renewal" — links the record to those entities.
 * Reusable anywhere content should reference entities (notes · descriptions · comments).
 */
export interface MentionCandidate { entity_type: string; id: string; label: string; sub?: string }
export interface Mention { entity_type: string; id: string; label: string }
export interface MentionFieldProps {
  value: string
  onChange: (text: string) => void
  mentions: Mention[]
  onMentionsChange: (m: Mention[]) => void
  onSearch: (query: string) => Promise<MentionCandidate[]>
  placeholder?: string
  rows?: number
  className?: string
  /* The props <Field> clones onto its single child (UX-04), plus the caller's
     own name. They land on the TEXTAREA — the node a person focuses and a
     screen reader announces — because `id` has to be where `<label htmlFor>`
     looks. They were dropped: not declared, not forwarded, and a hyphenated
     JSX attribute is not type-checked against a component's props, so a
     caller passing `aria-label` got no error and no name, and a MentionField
     inside a Field had a label pointing at nothing (#2406 kit-defects-b). */
  id?: string
  'aria-label'?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  'aria-required'?: boolean
}

export function MentionField({
  value, onChange, mentions, onMentionsChange, onSearch, placeholder, rows = 4, className, ...wiring
}: MentionFieldProps) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [anchor, setAnchor] = useState(0)        // caret index where the @token starts
  const [hits, setHits] = useState<MentionCandidate[]>([])
  const [active, setActive] = useState(0)
  const listId = `${useId()}-mentions`
  const optionId = (i: number) => `${listId}-${i}`
  const listed = open && hits.length > 0

  // Detect an @token immediately before the caret and drive the popover.
  const scan = useCallback((text: string, caret: number) => {
    const before = text.slice(0, caret)
    const m = /(^|\s)@([^\s@]*)$/.exec(before)
    if (m) { const frag = m[2] ?? ''; setAnchor(caret - frag.length); setQuery(frag); setOpen(true) }
    else { setOpen(false); setQuery('') }
  }, [])

  useEffect(() => {
    if (!open) return
    let live = true
    const t = setTimeout(() => void (async () => {
      try { const r = await onSearch(query); if (live) { setHits(r.slice(0, 8)); setActive(0) } }
      catch { if (live) setHits([]) }
    })(), 140)
    return () => { live = false; clearTimeout(t) }
  }, [open, query, onSearch])

  const pick = (c: MentionCandidate) => {
    const el = ref.current; if (!el) return
    const caret = el.selectionStart
    const next = value.slice(0, anchor) + c.label + ' ' + value.slice(caret)
    onChange(next)
    if (!mentions.some((m) => m.entity_type === c.entity_type && m.id === c.id))
      onMentionsChange([...mentions, { entity_type: c.entity_type, id: c.id, label: c.label }])
    setOpen(false)
    queueMicrotask(() => { el.focus(); const p = anchor + c.label.length + 1; el.setSelectionRange(p, p) })
  }

  const onKey = (e: React.KeyboardEvent) => {
    if (!open || hits.length === 0) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => (a + 1) % hits.length) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => (a - 1 + hits.length) % hits.length) }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pick(hits[active]!) }
    else if (e.key === 'Escape') { setOpen(false) }
  }

  /* ── THE COMBOBOX CONTRACT (#2406 b3-controls; #1466 C1 recorded it) ──────
     The popover existed only visually: the textarea said nothing about it, so
     a screen-reader user typing "@Ac" was never told a list had opened, nor
     which entry Enter would insert. The editor is now the combobox that owns
     the list — `aria-expanded` while the list is showing, `aria-controls` to
     it, `aria-activedescendant` naming the entry the arrows moved to (focus
     never leaves the text being written) — and every option carries the id
     that points at. The "no matches" line is a status, not an option. */
  return (
    <div className={cx(s.wrap, className)}>
      <Textarea ref={ref} rows={rows} placeholder={placeholder ?? 'Write… type @ to link an account, deal, contract…'}
        {...wiring}
        role="combobox" aria-autocomplete="list" aria-haspopup="listbox"
        aria-expanded={listed} aria-controls={listed ? listId : undefined}
        aria-activedescendant={listed ? optionId(active) : undefined}
        value={value} data-testid="mention-input"
        onChange={(e) => { onChange(e.target.value); scan(e.target.value, e.target.selectionStart) }}
        onKeyDown={onKey}
        onClick={(e) => scan(value, (e.target as HTMLTextAreaElement).selectionStart)} />
      {open && (hits.length === 0
        ? <div className={s.pop} role="status" data-testid="mention-pop">
            <div className={s.empty}>No matches for “{query}”…</div>
          </div>
        : (
        <div className={s.pop} role="listbox" id={listId} aria-label="Mention suggestions" data-testid="mention-pop">
          {hits.map((c, i) => (
              <button type="button" key={c.entity_type + c.id} id={optionId(i)} role="option" aria-selected={i === active}
                tabIndex={-1}
                className={cx(s.opt, i === active && s.optOn)} data-testid={`mention-opt-${c.id}`}
                onMouseEnter={() => setActive(i)} onMouseDown={(e) => { e.preventDefault(); pick(c) }}>
                <span className={s.optLabel}>{c.label}</span>
                <span className={s.optType}>{c.sub ?? c.entity_type.replace(/_/g, ' ')}</span>
              </button>
            ))}
        </div>
        ))}
      {mentions.length > 0 && (
        <div className={s.chips} data-testid="mention-chips">
          {mentions.map((m) => (
            <span key={m.entity_type + m.id} className={s.chip}>
              <span className={s.chipGlyph}>@</span>{m.label}
              <button type="button" className={s.chipX} aria-label={`Unlink ${m.label}`}
                onClick={() => onMentionsChange(mentions.filter((x) => !(x.entity_type === m.entity_type && x.id === m.id)))}>✕</button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
